<template>
  <div class="email-auth-container">
    <!-- Email Input Step -->
    <div v-if="currentStep === 'email'" class="auth-step">
      
      <form @submit.prevent="handleEmailSubmit" class="space-y-4">
        <div>
          <input
            v-model="formData.email"
            type="email"
            placeholder="Enter your email"
            class="w-full px-4 py-2 rounded-3xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30"
            :disabled="loading"
            required
          />
        </div>

        <button
          type="submit"
          class="w-full bg-white text-gray-900 font-semibold py-[10px] px-6 rounded-3xl hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="loading || !isValidEmail(formData.email)"
        >
          <span v-if="loading" class="flex items-center justify-center">
            <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Checking...
          </span>
          <span v-else>Continue</span>
        </button>
      </form>
    </div>

    <!-- Name Input Step (for registration) -->
    <div v-if="currentStep === 'name'" class="auth-step">
      <h3 class="text-lg font-semibold text-white mb-2 text-center">Create Account</h3>
      <p class="text-white/80 text-sm mb-4 text-center">Email not found. Let's create your account.</p>
      
      <form @submit.prevent="handleNameSubmit" class="space-y-4">
        <div>
          <input
            v-model="formData.name"
            type="text"
            placeholder="Enter your full name"
            class="w-full px-4 py-2 rounded-3xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30"
            :disabled="loading"
            required
          />
        </div>
        
        <button
          type="submit"
          class="w-full bg-white text-gray-900 font-semibold py-[10px] px-6 rounded-3xl hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="loading || !isValidName(formData.name)"
        >
          <span v-if="loading" class="flex items-center justify-center">
            <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Sending Code...
          </span>
          <span v-else>Send Code</span>
        </button>
        
        <button
          type="button"
          @click="goBack"
          class="w-full bg-transparent border border-white/30 text-white font-semibold py-[10px] px-6 rounded-3xl hover:bg-white/10 transition-colors"
        >
          Back
        </button>
      </form>
    </div>

    <!-- OTP Input Step -->
    <div v-if="currentStep === 'otp'" class="auth-step">
      <h3 class="text-lg font-semibold text-white mb-2 text-center">Verification Code</h3>
      <p class="text-white/80 text-sm mb-4 text-center">
        Enter the 6-digit code sent to {{ formData.email }}
      </p>
      
      <form @submit.prevent="handleOtpSubmit" class="space-y-4">
        <div>
          <input
            v-model="formData.otp"
            type="text"
            placeholder="000000"
            maxlength="6"
            class="w-full px-4 py-2 rounded-3xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 text-center text-2xl tracking-widest"
            :disabled="loading"
            required
          />
        </div>
        
        <button
          type="submit"
          class="w-full bg-white text-gray-900 font-semibold py-[10px] px-4 rounded-3xl hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="loading || !isValidOTP(formData.otp)"
        >
          <span v-if="loading">Verifying...</span>
          <span v-else>Continue</span>
        </button>
        
        <button
          type="button"
          @click="goBack"
          class="w-full bg-transparent border border-white/30 text-white font-semibold py-[10px] px-6 rounded-3xl hover:bg-white/10 transition-colors"
        >
          Back
        </button>
      </form>
    </div>

    <!-- Password Input Step -->
    <div v-if="currentStep === 'password'" class="auth-step">
      <h3 class="text-lg font-semibold text-white mb-2 text-center">
        {{ authAction === 'login' ? 'Enter Password' : 'Create Password' }}
      </h3>
      <p class="text-white/80 text-sm mb-4 text-center">
        {{ authAction === 'login' ? 'Enter your password to sign in' : 'Create a secure password for your account' }}
      </p>
      
      <form @submit.prevent="handlePasswordSubmit" class="space-y-4">
        <div>
          <input
            v-model="formData.password"
            type="password"
            placeholder="Enter your password"
            class="w-full px-4 py-2 rounded-3xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30"
            :disabled="loading"
            required
          />
        </div>
        
        <button
          type="submit"
          class="w-full bg-white text-gray-900 font-semibold py-[10px] px-6 rounded-3xl hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="loading || !isValidPassword(formData.password)"
        >
          <span v-if="loading" class="flex items-center justify-center">
            <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ authAction === 'login' ? 'Signing in...' : 'Creating account...' }}
          </span>
          <span v-else>{{ authAction === 'login' ? 'Sign In' : 'Create Account' }}</span>
        </button>
        
        <button
          type="button"
          @click="goBack"
          class="w-full bg-transparent border border-white/30 text-white font-semibold py-[10px] px-6 rounded-3xl hover:bg-white/10 transition-colors"
        >
          Back
        </button>
      </form>
    </div>

    <!-- Error Message -->
    <div v-if="error" class="mt-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
      <p class="text-red-200 text-sm text-center">{{ error }}</p>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { isValidEmail, validatePassword, validateOTP, validateName } from '@/services/emailAuth'
import { executeAndVerifyRecaptcha } from '@/services/recaptchaService'

export default {
  name: 'EmailAuth',
  setup() {
    const store = useStore()
    const router = useRouter()
    
    const currentStep = ref('email')
    const authAction = ref('login') // 'login' or 'register'
    const loading = computed(() => store.getters.isLoading)
    const error = computed(() => store.getters.getError)
    
    const formData = ref({
      email: '',
      name: '',
      otp: '',
      password: ''
    })

    // reCAPTCHA state
    const recaptchaVerificationId = ref(null)
    
    const handleEmailSubmit = async () => {
      try {
        // Execute invisible reCAPTCHA and verify
        const recaptchaResult = await executeAndVerifyRecaptcha('email_check', formData.value.email)

        if (!recaptchaResult.success) {
          store.commit('SET_ERROR', 'reCAPTCHA verification failed. Please try again.')
          return
        }

        // Store verification ID for later use
        recaptchaVerificationId.value = recaptchaResult.verificationId

        const response = await store.dispatch('checkEmailForAuth', {
          email: formData.value.email,
          recaptchaVerificationId: recaptchaResult.verificationId
        })

        if (response.action === 'login') {
          authAction.value = 'login'
          currentStep.value = 'otp'
        } else if (response.action === 'register') {
          authAction.value = 'register'
          currentStep.value = 'name'
        }
      } catch (error) {
        // Error is handled by the store or shown directly
        store.commit('SET_ERROR', error.message || 'reCAPTCHA verification failed. Please try again.')
      }
    }
    
    const handleNameSubmit = async () => {
      try {
        await store.dispatch('registerUserWithName', {
          email: formData.value.email,
          name: formData.value.name
        })
        currentStep.value = 'otp'
      } catch (error) {
        // Error is handled by the store
      }
    }
    
    const handleOtpSubmit = () => {
      currentStep.value = 'password'
    }
    
    const handlePasswordSubmit = async () => {
      try {
        if (authAction.value === 'login') {
          await store.dispatch('loginWithEmailAuth', {
            email: formData.value.email,
            otp: formData.value.otp,
            password: formData.value.password
          })
        } else {
          await store.dispatch('registerWithEmailAuth', {
            email: formData.value.email,
            name: formData.value.name,
            otp: formData.value.otp,
            password: formData.value.password
          })
        }
        
        // Redirect to home on success
        router.push('/')
      } catch (error) {
        // Error is handled by the store
      }
    }
    
    const goBack = () => {
      if (currentStep.value === 'name') {
        currentStep.value = 'email'
      } else if (currentStep.value === 'otp') {
        currentStep.value = authAction.value === 'register' ? 'name' : 'email'
      } else if (currentStep.value === 'password') {
        currentStep.value = 'otp'
      }
    }
    
    const isValidName = (name) => {
      return validateName(name).isValid
    }
    
    const isValidPassword = (password) => {
      return validatePassword(password).isValid
    }
    
    const isValidOTP = (otp) => {
      return validateOTP(otp)
    }

    return {
      currentStep,
      authAction,
      loading,
      error,
      formData,
      handleEmailSubmit,
      handleNameSubmit,
      handleOtpSubmit,
      handlePasswordSubmit,
      goBack,
      isValidEmail,
      isValidName,
      isValidPassword,
      isValidOTP
    }
  }
}
</script>

<style scoped>
.email-auth-container {
  width: 100%;
  max-width: 400px;
}

.auth-step {
  width: 100%;
}

/* Custom input styling for OTP */
input[type="text"]:placeholder-shown {
  font-family: monospace;
}
</style>
